import type { Ingredient } from '@/service/recipe/types';
import {
  type Grocery,
  GrocerySchema,
  type GroceryUnit,
} from '@/service/groceries/types';

// What adding ingredients changes on the grocery list: new items, and
// amounts added to items already on it. Reverting it undoes exactly that.
export interface GroceryPlan {
  add: Grocery[];
  increase: { id: string; by: number }[];
}

// An ingredient to add, remembering the recipe it came from
export type PlannedIngredient = Ingredient & { recipeId?: string };

function key(text: string, unit: GroceryUnit | undefined): string {
  return `${text.trim().toLowerCase().replace(/\s+/g, ' ')}|${unit ?? ''}`;
}

// Same ingredient (ignoring case and spacing) in the same unit adds up;
// anything else, or an item already ticked off, gets a new item
export function planAdditions(
  existing: Grocery[],
  ingredients: PlannedIngredient[],
  now: number = Date.now(),
): GroceryPlan {
  const onList = new Map(
    existing
      .filter(({ done }) => !done)
      .map((item) => [key(item.text, item.unit), item.id]),
  );
  const added = new Map<string, Grocery>();
  const increases = new Map<string, number>();

  for (const { text, amount, unit, recipeId } of ingredients) {
    if (!text.trim()) continue;
    const itemKey = key(text, unit);
    const by = amount > 0 ? amount : 0;
    const planned = added.get(itemKey);
    const id = onList.get(itemKey);
    if (planned) {
      planned.amount = (planned.amount ?? 0) + by || undefined;
    } else if (id) {
      increases.set(id, (increases.get(id) ?? 0) + by);
    } else {
      added.set(
        itemKey,
        GrocerySchema.parse({
          text: text.trim(),
          amount: by || undefined,
          unit,
          recipeId,
          // Consecutive timestamps keep the recipe's order
          createdAt: now + added.size,
        }),
      );
    }
  }

  return {
    add: [...added.values()],
    increase: [...increases]
      .filter(([, by]) => by > 0)
      .map(([id, by]) => ({ id, by })),
  };
}
